import React, { useState, useEffect, useRef } from 'react';
import { Typography, Spin, Tooltip, Tag, Button } from 'antd';
import {
  ShoppingOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SendOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  RollbackOutlined,
  ArrowRightOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  CarryOutOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import './Dashboard.css';

const { Title, Text } = Typography;

const STATUS_CONFIG = [
  {
    key: 'open',
    label: 'Open',
    icon: <ShoppingOutlined />,
    color: '#2563eb',
    colorEnd: '#60a5fa',
    bg: '#eff6ff',
    ring: '#dbeafe',
  },
  {
    key: 'pending',
    label: 'Pending',
    icon: <ClockCircleOutlined />,
    color: '#d97706',
    colorEnd: '#fbbf24',
    bg: '#fffbeb',
    ring: '#fef3c7',
  },
  {
    key: 'ready',
    label: 'Ready (send to QC)',
    icon: <CheckCircleOutlined />,
    color: '#059669',
    colorEnd: '#34d399',
    bg: '#ecfdf5',
    ring: '#d1fae5',
  },
  {
    key: 'repair',
    label: 'Repair',
    icon: <ToolOutlined />,
    color: '#c026d3',
    colorEnd: '#e879f9',
    bg: '#fdf4ff',
    ring: '#f5d0fe',
  },
  {
    key: 'dispatch',
    label: 'Dispatch',
    icon: <SendOutlined />,
    color: '#0891b2',
    colorEnd: '#22d3ee',
    bg: '#ecfeff',
    ring: '#cffafe',
  },
  {
    key: 'completed',
    label: 'Completed',
    icon: <CheckCircleOutlined />,
    color: '#047857',
    colorEnd: '#10b981',
    bg: '#ecfdf5',
    ring: '#d1fae5',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    icon: <CloseCircleOutlined />,
    color: '#dc2626',
    colorEnd: '#f87171',
    bg: '#fef2f2',
    ring: '#fecaca',
  },
  {
    key: 'return',
    label: 'Return',
    icon: <RollbackOutlined />,
    color: '#7c3aed',
    colorEnd: '#a78bfa',
    bg: '#f5f3ff',
    ring: '#ddd6fe',
  },
];

/* ---- Animated counter ---- */
function useAnimatedCount(target, duration = 900) {
  const [count, setCount] = useState(0);
  const frameRef = useRef();
  useEffect(() => {
    if (target == null || target === 0) { setCount(0); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setCount(Math.round(target * eased));
      if (p < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);
  return count;
}

function Num({ value }) {
  return <>{useAnimatedCount(value)}</>;
}

/* ---- Ring progress (SVG) ---- */
function Ring({ percent, color, colorEnd, trackColor, size = 58, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (circ * Math.min(percent, 100)) / 100;
  return (
    <svg width={size} height={size} className="dash-ring" viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id={`rg-${color.replace('#', '')}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={colorEnd} />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={trackColor} strokeWidth={stroke} opacity="0.45" />
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={`url(#rg-${color.replace('#', '')})`}
        strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="dash-ring-fill" />
    </svg>
  );
}

/* ---- helpers ---- */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function fmtDate() {
  return new Date().toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function getDefaultStatusFilter(user) {
  const roleValues = user?.roles?.map((r) => r.value) ?? [];
  if(user?.roles?.some((r) => r.value === 'admin' || r.value === 'manager' || r.value === 'read-only')) {
    return undefined;
  }
  if (roleValues.includes('stock-manager')) {
    return ['dispatch'];
  } else if (roleValues.some((v) => v === 'order-manager' || v === 'stone-manager')) {
    return ['open', 'pending','repair'];
  } else if (roleValues.includes('qc-manager')) {
    return ['ready'];
  }
  return undefined;
}

/* ======== DASHBOARD ======== */
export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.authReducer);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingRecurring, setPendingRecurring] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/tasks/my/pending-recurring');
        setPendingRecurring(res.data ?? []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get('/orders/dashboard');
        setStats(res?.data ?? {});
      } catch (err) {
        dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load dashboard', type: 'error' }));
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  const total = stats?.total ?? 0;
  const active = (stats?.open ?? 0) + (stats?.pending ?? 0);
  const readyToShip = stats?.ready ?? 0;
  const fulfilled = stats?.dispatch ?? 0;

  const statusFilter = getDefaultStatusFilter(user);
  const breakdownConfig = statusFilter?.length
    ? STATUS_CONFIG.filter((s) => statusFilter.includes(s.key))
    : STATUS_CONFIG;

  const roleValues = user?.roles?.map((r) => r.value) ?? [];
  const isQcOnly = roleValues.includes('qc-manager') && !roleValues.includes('admin') && !roleValues.includes('manager');
  const isStockManager = roleValues.includes('stock-manager');
  const isOrderOrStoneManager = roleValues.some((v) => v === 'order-manager' || v === 'stone-manager');
  const hideTotalOrders = isQcOnly || isStockManager || isOrderOrStoneManager;

  return (
    <div className="dash">
      {/* ---- Greeting ---- */}
      <div className="dash-greet">
        <div>
          <Title level={3} className="dash-greet-title">
            {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
          </Title>
          <Text className="dash-greet-sub">{fmtDate()}</Text>
        </div>
      </div>

      {/* ---- Hero banner (hidden for QC-only, stock-manager, order-manager, stone-manager) ---- */}
      {!hideTotalOrders && (
      <div className="dash-hero">
        <div className="dash-hero-shimmer" />
        <div className="dash-hero-content">
          {loading ? (
            <div className="dash-hero-loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* left: total */}
              <div className="dash-hero-total">
                <div className="dash-hero-icon"><ShoppingOutlined /></div>
                <div>
                  <div className="dash-hero-num"><Num value={total} /></div>
                  <div className="dash-hero-label">Total Orders</div>
                </div>
              </div>

              {/* right: quick metrics */}
              <div className="dash-hero-metrics">
                <div className="dash-hero-metric">
                  <span className="dash-hero-m-icon dash-hero-m-active"><ThunderboltOutlined /></span>
                  <div>
                    <div className="dash-hero-m-val"><Num value={active} /></div>
                    <div className="dash-hero-m-label">Active</div>
                  </div>
                </div>
                <div className="dash-hero-metric-divider" />
                <div className="dash-hero-metric">
                  <span className="dash-hero-m-icon dash-hero-m-ready"><CheckCircleOutlined /></span>
                  <div>
                    <div className="dash-hero-m-val"><Num value={readyToShip} /></div>
                    <div className="dash-hero-m-label">Ready to Ship</div>
                  </div>
                </div>
                <div className="dash-hero-metric-divider" />
                <div className="dash-hero-metric">
                  <span className="dash-hero-m-icon dash-hero-m-shipped"><RocketOutlined /></span>
                  <div>
                    <div className="dash-hero-m-val"><Num value={fulfilled} /></div>
                    <div className="dash-hero-m-label">Shipped</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* distribution bar - only when loaded */}
        {!loading && (
        <div className="dash-hero-bar-wrap">
          <div className="dash-hero-bar">
            {breakdownConfig.map((s) => {
              const v = stats?.[s.key] ?? 0;
              const pct = total > 0 ? (v / total) * 100 : 0;
              if (pct === 0) return null;
              return (
                <Tooltip key={s.key} title={`${s.label}: ${v} (${pct.toFixed(1)}%)`}>
                  <div className="dash-hero-bar-seg" style={{ width: `${Math.max(pct, 1.5)}%`, background: `linear-gradient(135deg, ${s.color}, ${s.colorEnd})` }} />
                </Tooltip>
              );
            })}
          </div>
          <div className="dash-hero-bar-legend">
            {breakdownConfig.map((s) => (
              <span key={s.key} className="dash-hero-bar-leg">
                <span className="dash-hero-bar-dot" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        )}

      </div>
      )}

      {pendingRecurring.length > 0 && (
        <div className="dash-pending-section">
          <div className="dash-pending-header">
            <div className="dash-pending-header-left">
              <ClockCircleOutlined className="dash-pending-header-icon" />
              <span className="dash-pending-header-title">Pending Tasks</span>
              <span className="dash-pending-header-badge">{pendingRecurring.length}</span>
            </div>
            <Button
              type="link"
              className="dash-pending-header-link"
              onClick={() => navigate('/my-tasks')}
            >
              View all <ArrowRightOutlined />
            </Button>
          </div>
          <div className="dash-pending-scroll">
            {pendingRecurring.map((task) => (
              <div className="dash-pending-item" key={task._id}>
                <div className="dash-pending-item-icon">
                  <CarryOutOutlined />
                </div>
                <div className="dash-pending-item-body">
                  <div className="dash-pending-item-title">{task.title}</div>
                  {task.rewardPoint > 0 && (
                    <Tag color="gold" className="dash-pending-item-tag">
                      <TrophyOutlined /> {task.rewardPoint} pts
                    </Tag>
                  )}
                </div>
                <Button
                  size="small"
                  type="primary"
                  className="dash-pending-item-btn"
                  onClick={() => navigate('/my-tasks')}
                >
                  Submit
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Section header ---- */}
      <div className="dash-sec-head">
        <AppstoreOutlined className="dash-sec-ico" />
        <span>Order Breakdown</span>
      </div>

      {/* ---- Cards grid ---- */}
      <div className="dash-grid">
        {breakdownConfig.map((item, idx) => {
          const count = stats?.[item.key] ?? 0;
          const pct = total > 0 ? (count / total) * 100 : 0;

          return (
            <div
              className="dash-card"
              key={item.key}
              style={{ animationDelay: `${idx * 70}ms` }}
              onClick={() => navigate(`/orders?status=${item.key}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/orders?status=${item.key}`); }}
            >
              {/* glow on hover */}
              <div className="dash-card-glow" style={{ background: item.color }} />

              <div className="dash-card-top">
                <div className="dash-card-ring-wrap">
                  <Ring percent={pct} color={item.color} colorEnd={item.colorEnd} trackColor={item.ring} />
                  <span className="dash-card-ring-pct" style={{ color: item.color }}>
                    {pct.toFixed(0)}%
                  </span>
                </div>
                <div className="dash-card-icon" style={{ color: item.color, background: item.bg }}>
                  {item.icon}
                </div>
              </div>

              <div className="dash-card-num" style={{ color: item.color }}>
                <Num value={count} />
              </div>
              <div className="dash-card-label">{item.label} Orders</div>

              <div className="dash-card-foot" style={{ color: item.color }}>
                <span>View orders</span>
                <ArrowRightOutlined className="dash-card-arrow" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
